// AI Generated Component
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { SpellingRepoService } from '@client/app/services/api/spelling-repo/spelling-repo.service';
import { LoadingService } from '@monsido/angular-shared-components';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';

@Component({
    selector: 'table-dictionary',
    standalone: false,
    template: `
        <mon-action-menu [monActionMenuItems]="dropdownOptions">
            <span translate>Action</span>
        </mon-action-menu>
    `,
})
export class TableDictionaryComponent implements OnInit {
    @Input() word: any;
    @Input() language?: any;
    @Input() classification?: any;
    @Input() wordId?: any;
    @Output() callback: EventEmitter<any> = new EventEmitter();

    dropdownOptions: any[] = [];
    dialog: any;

    constructor (
        private spellingRepoService: SpellingRepoService,
        private loadingService: LoadingService,
        private translateService: TranslateService,
        private monEventService: MonEventService,
        private monModalService: MonModalService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    activate () {
        const dropdown = [
            {
                label: this.translateService.getString('Confirm as misspelling'),
                icon: 'faCheck',
                action: this.confirmAsMisspelling.bind(this),
                eventParams: { action: 'spellcheck-confirm-misspelling' },
                shouldShow: this.classification === 'potential',
            },
            {
                label: this.translateService.getString('Add to dictionary'),
                icon: 'faBook',
                action: this.addToDictionary.bind(this),
                eventParams: { action: 'spellcheck-add-to-dictionary' },
            },
            {
                label: this.translateService.getString('Add to dictionary for all languages'),
                icon: 'faAtlas',
                action: this.addToAllDictionaries.bind(this),
                eventParams: { action: 'spellcheck-add-to-all-dictionary' },
            },
            {
                label: this.translateService.getString('Ignore misspelling'),
                icon: 'faEyeSlash',
                action: this.ignoreMisspelling.bind(this),
                eventParams: { action: 'spellcheck-ignore-misspellings' },
            },
            {
                label: this.translateService.getString('Mark as fixed'),
                icon: 'faCheck',
                action: this.markMisspellingAsFixed.bind(this),
                eventParams: { action: 'spellcheck-markasfix-misspellings' },
            },
        ];

        this.dropdownOptions = dropdown.map(item => {
            if (item.shouldShow !== false) {
                return {
                    label: item.label,
                    leftIcon: item.icon,
                    action: () => {
                        this.monEventService.run('actionTrigger', item.eventParams);
                        item.action();
                    },
                };
            }
            return undefined;
        }).filter(Boolean);
    }

    promptConfirmation (message: string, callback: () => void) {
        this.monModalService.confirm(message)
            .then(callback)
            .catch(() => {});
    }

    addToDictionary () {
        const message = this.translateService.getString(
            'Are you sure you want to add the word <strong>[[word]]</strong> to the dictionary?',
            { word: this.word },
        );
        this.promptConfirmation(message, () => {
            const params = {
                word: this.word,
                language: this.language,
                classification: 'dictionary',
            };

            this.dialog = this.loadingService.service(
                this.translateService.getString('Please wait - Adding word to dictionary'),
            );
            this.spellingRepoService.addToDictionary(params).then(() => {
                if (this.callback) {
                    this.callback.emit();
                }
                this.dialog.close();
            }, () => {});
        });
    }

    markMisspellingAsFixed () {
        const message = this.translateService.getString(
            'Are you sure you want to mark the misspelling <strong>[[word]]</strong> as fixed?',
            { word: this.word },
        );
        this.promptConfirmation(message, () => {
            this.dialog = this.loadingService.service(
                this.translateService.getString('Please wait - Marking word as fixed'),
            );
            this.spellingRepoService.destroySpellingError(Number(this.wordId)).then(() => {
                if (this.callback) {
                    this.callback.emit();
                }
                this.dialog.close();
            }, () => {});
        });
    }

    addToAllDictionaries () {
        const message = this.translateService.getString(
            'Are you sure you want to add the word <strong>[[word]]</strong> to the dictionary for all languages?',
            { word: this.word },
        );
        this.promptConfirmation(message, () => {
            const params = {
                word: this.word,
                classification: 'dictionary',
            };

            this.dialog = this.loadingService.service(
                this.translateService.getString('Please wait - Adding words to dictionary'),
            );
            this.spellingRepoService.addToDictionary(params).then(() => {
                if (this.callback) {
                    this.callback.emit();
                }
                this.dialog.close();
            }, () => {});
        });
    }

    ignoreMisspelling () {
        const message = this.translateService.getString(
            'Are you sure you want to ignore the misspelling <strong>[[word]]</strong> for all languages?',
            { word: this.word },
        );
        this.promptConfirmation(message, () => {
            const params = {
                word: this.word,
                language: this.language,
                classification: 'ignore',
            };

            this.dialog = this.loadingService.service(
                this.translateService.getString('Please wait - Adding word to ignored list'),
            );
            this.spellingRepoService.addToDictionary(params).then(() => {
                if (this.callback) {
                    this.callback.emit();
                }
                this.dialog.close();
            }, () => {});
        });
    }

    confirmAsMisspelling () {
        const message = this.translateService.getString(
            'Are you sure you want to confirm the word <strong>[[word]]</strong> as misspelling?',
            { word: this.word },
        );
        this.promptConfirmation(message, () => {
            this.dialog = this.loadingService.service(
                this.translateService.getString('Please wait - Confirming misspelling'),
            );
            this.spellingRepoService.confirmWord(Number(this.wordId), { id: Number(this.wordId), labels: [] }).then(() => {
                if (this.callback) {
                    this.callback.emit();
                }
                this.dialog.close();
            }, () => {});
        });
    }
}
